// Datas no backend/DB ficam sempre em ISO (YYYY-MM-DD). Esta função é só para
// exibição na tela, no padrão brasileiro Dia/Mês/Ano. Não usar em <input type="date">
// (esses precisam continuar em ISO, é o formato exigido pelo próprio HTML).
export function formatDate(isoDate) {
  if (!isoDate) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate);
  if (!m) return isoDate;
  const [, year, month, day] = m;
  return `${day}/${month}/${year}`;
}

// Lote 99: textos de publicação já gravados trazem "Disponibilização: AAAA-MM-DD" (cabeçalho
// montado a partir da API do DJEN). Para exibir/copiar, troca por dia/mês/ano.
export function brDatesInText(text) {
  if (!text) return text || "";
  return String(text).replace(
    /(Disponibiliza(?:ç|c)(?:ã|a)o|Publica(?:ç|c)(?:ã|a)o):\s*(\d{4})-(\d{2})-(\d{2})/g,
    (_, label, y, m, d) => `${label}: ${d}/${m}/${y}`,
  );
}

// Chave "YYYY-MM" para agrupar uma lista por mês (Agenda, Próximas audiências do Painel).
// Itens sem data caem no grupo "sem-data", sempre exibido por último.
export function monthKey(isoDate) {
  if (!isoDate) return "sem-data";
  const m = /^(\d{4})-(\d{2})/.exec(isoDate);
  return m ? `${m[1]}-${m[2]}` : "sem-data";
}

// Rótulo "setembro de 2026" a partir da chave "YYYY-MM" gerada por monthKey.
export function monthLabel(key) {
  if (!key || key === "sem-data") return "Sem data";
  const [year, month] = key.split("-");
  const d = new Date(Number(year), Number(month) - 1, 1);
  const label = d.toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
  return label.replace(" de ", " de "); // mantém formato nativo pt-BR "setembro de 2026"
}

// Agrupa uma lista de itens por mês (a partir de getDate(item)), preservando a ordem
// relativa dos itens dentro de cada grupo, e ordena os grupos conforme `order`
// ("asc" = mais antigo primeiro, "desc" = mais recente primeiro). Grupos "sem-data"
// sempre ficam por último.
export function groupByMonth(items, getDate, order = "asc") {
  const map = new Map();
  for (const item of items) {
    const key = monthKey(getDate(item));
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(item);
  }
  const entries = Array.from(map.entries());
  entries.sort((a, b) => {
    if (a[0] === "sem-data") return 1;
    if (b[0] === "sem-data") return -1;
    return order === "desc" ? (a[0] < b[0] ? 1 : -1) : a[0] < b[0] ? -1 : 1;
  });
  return entries;
}

// Palavras que ficam em minúsculo quando não são a primeira da frase — preposições comuns
// em nomes/textos jurídicos em português, e o "x" usado como separador entre partes
// ("Fulano x Sicrano"). Lista curta de propósito, só o que aparece de fato nesses campos.
const LOWERCASE_WORDS = new Set(["de", "da", "do", "das", "dos", "e", "x"]);

// Padroniza a caixa de nomes de partes e Comarca/Vara (Lote 47b, pedido do Robson,
// 24/09/2026): "sempre minúsculas, exceto as primeiras letras do nome" — hoje o texto vem
// cru do DJEN/DataJud ou do que o próprio advogado digitou, às vezes em CAIXA ALTA, às
// vezes já bem formatado, sem padrão. Capitaliza a primeira letra de cada palavra e
// preserva o resto em minúsculo, exceto preposições curtas (fica "Vara do Trabalho", não
// "Vara Do Trabalho") e a primeira palavra da frase (sempre capitalizada, mesmo se for uma
// dessas preposições). Uma palavra com número (ex. "3ª", "TRT15", "CON1") não é mexida —
// preserva como veio, pra não estragar ordinais/siglas com código.
export function toTitleCasePt(text) {
  if (!text) return text || "";
  let sawFirstWord = false;
  return text
    .split(/(\s+)/) // mantém os espaços originais na saída (split com grupo de captura)
    .map((chunk) => {
      if (chunk === "" || /^\s+$/.test(chunk)) return chunk; // espaço — mantém igual
      if (/\d/.test(chunk)) { sawFirstWord = true; return chunk; } // ordinal/sigla — não mexe
      const lower = chunk.toLocaleLowerCase("pt-BR");
      const isFirstWord = !sawFirstWord;
      sawFirstWord = true;
      if (!isFirstWord && LOWERCASE_WORDS.has(lower)) return lower;
      return lower.charAt(0).toLocaleUpperCase("pt-BR") + lower.slice(1);
    })
    .join("");
}

// Aplica toTitleCasePt ao campo "partes" (ex.: "JOAO ALFREDO RODRIGUES x MARIA SILVA" ->
// "Joao Alfredo Rodrigues x Maria Silva"), usado nas tabelas de Agenda e Painel.
export function formatParties(parties) {
  return parties ? toTitleCasePt(parties) : parties;
}

// Junta Vara e Comarca (campos separados desde o Lote 32) numa única célula de exibição,
// usada nas tabelas de Agenda e Painel — "2ª Vara do Trabalho — São Paulo". Mostra só o
// que tiver preenchido, e "—" quando nenhum dos dois existir. Cada parte passa por
// toTitleCasePt (Lote 47b) pra não ficar em caixa alta quando vem assim do DJEN/DataJud.
export function formatCourtDistrict(court, courtDistrict) {
  const parts = [court, courtDistrict].map((v) => toTitleCasePt((v || "").trim())).filter(Boolean);
  return parts.length ? parts.join(" — ") : "—";
}

// Lote 74 — data em que a intimação/publicação saiu (para conferir a contagem do prazo).
// Vem de hearing.published_at (informada no evento / lida do texto colado) ou, para o que
// chegou do DJEN, da própria publicação. Eventos manuais e do DataJud não têm uma data de
// publicação real (published_at ali é só o dia do cadastro), então ficam sem.
export function publicationDate(pub) {
  if (!pub) return null;
  const own = pub.hearing?.published_at;
  if (own) return own;
  if (pub.source === "djen" && pub.external_id && pub.published_at) return pub.published_at;
  if (pub.source === "djen_auto_prazo" && pub.published_at) return pub.published_at;
  return null;
}
